import RequestUtilitario, { BasicMutationAxiosType, BasicQueryType, QueryBasicaParam } from "../../../Utils/RequisicaoUtilitario";
import Titulo from "../../../Models/Financeiro/Titulo/Titulo";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Axios } from "../../Axios";
import { AxiosResponse } from "axios";
import Origem from "../../../Models/Financeiro/Titulo/Origem";
import Pagina, { IPagina, IPaginaInfo, PaginaInfo } from "../../../Models/Utils/Pagina/Pagina";
import { RecursivePartial } from "@/utility-types";

export function useSalvar({
    onSuccess,
    ...rest
}:BasicMutationAxiosType<
    { titulo:Titulo, isAlteracao:boolean, isAReceber:boolean }, 
    {},
    number|void
>) {
    const queryClient = useQueryClient();

    return useMutation({
        ...rest,
        mutationFn : ({
            isAlteracao,
            isAReceber,
            titulo
        }) => {
            let resposta = null;
            if (isAlteracao)
                resposta = Axios.put<void>("financeiro/titulo", titulo);
            else
            {
                if (titulo.codFatura !== null && titulo.codFatura !== undefined && titulo.codFatura > 0 && !isNaN(titulo.codFatura))
                    resposta = Axios.post<number>(isAReceber ? "financeiro/titulo/areceber" : "financeiro/titulo/apagar", titulo);
                else 
                    resposta = Axios.post<number>(isAReceber ? "financeiro/titulo/areceber-com-fatura" : "financeiro/titulo/apagar-com-fatura", titulo);
            }
            
            return resposta.then(({data}) => data);
        },
        onSuccess : (...params) => {
            queryClient.invalidateQueries({
                queryKey : ["Financeiro.Titulo.Listar"]
            });

            onSuccess?.(...params)
        }
    });
}
export function useDesativar({
    onSuccess,
    ...rest
}:BasicMutationAxiosType<number, unknown, AxiosResponse<void>>) {
    const queryClient = useQueryClient();

    return useMutation({
        ...rest,
        mutationFn : (codigo) => Axios.delete<void>(`financeiro/titulo/${codigo}`),
        onSuccess : (...params) => {
            queryClient.invalidateQueries({
                queryKey : ["Financeiro.Titulo.Listar"]
            });

            onSuccess?.(...params);
        }
    });
}

export function useTituloDuplicar({
    onSuccess,
    ...rest
}:BasicMutationAxiosType<{codTituloBase:number, titulos:Titulo[]}, unknown, AxiosResponse<void>>) {
    const queryClient = useQueryClient();

    return useMutation({
        ...rest,
        mutationFn : ({codTituloBase, titulos}) => Axios.post<void>(`financeiro/titulo/${codTituloBase}/duplicar`, titulos),
        onSuccess : (...params) => {
            queryClient.invalidateQueries({
                queryKey : ["Financeiro.Titulo.Listar"]
            });

            onSuccess?.(...params);
        }
        //,
        // onSettled(data, error, variables, context) {
        //     console.log("Fez", data, error, variables, context);
        // },
    });
}

export type TituloDuplicarPropsType = Parameters<typeof useTituloDuplicar>[0];

export function useAtivar({
    onSuccess,
    ...rest
}:BasicMutationAxiosType<number, unknown, AxiosResponse<void>>) {
    const queryClient = useQueryClient();

    return useMutation({
        ...rest,
        mutationFn : (codigo) => Axios.patch<void>(`financeiro/titulo/ativar/${codigo}`),
        onSuccess : (...params) => {
            queryClient.invalidateQueries({
                queryKey : ["Financeiro.Titulo.Listar"]
            });

            onSuccess?.(...params);
        }
    });
}
export function useListar({
    titulo,
    ativo,
    receita,
    ...rest
}:BasicQueryType<AxiosResponse<Titulo[]>, {
    titulo?: RecursivePartial<Titulo>,
    ativo?:boolean,
    receita?:boolean
}>) {
    const queryString = RequestUtilitario.objectToQueryString({
        ...(titulo ?? {}),
        receita : receita,
        tituloAtivo : ativo
    });

    return useQuery({
        queryKey : ["Financeiro.Titulo.Listar", `Financeiro.Titulo.Listar?${queryString}`],
        queryFn : () => Axios.get<Titulo[]>(`financeiro/titulo?${queryString}`),
        staleTime : 1000 * 60 * 0.05, // 30 segundos,
        ...rest,
        select : ({data}) => data.map(m => {
            return {
                ...m,
                dataVencimento : m.dataVencimento === undefined ? m.dataVencimento : new Date(m.dataVencimento as any),
                //dataCredito : m.dataCredito == null ? null : new Date(m.dataCredito as any),
                dataPagamento : m.dataPagamento == null ? null : new Date(m.dataPagamento as any),
                dataCriacao : new Date(m.dataCriacao as any)
            } as Titulo;
        }),
    });
}
export function useListarPagina({
    pagina,
    titulo,
    ativo,
    receita,
    onFinish,
    ...rest
}:QueryBasicaParam<AxiosResponse<IPagina<Titulo>>, IPagina<Titulo>, {
    pagina?:IPaginaInfo,
    titulo?:Titulo,
    ativo?:boolean,
    receita?:boolean,
    onFinish?:() => void
}>) {
    const queryString = RequestUtilitario.objectToQueryString({
        pagina : (pagina ?? new PaginaInfo()),
        titulo : (titulo ?? {}),
        tituloAtivo : ativo,
        receita
    });

    return useQuery({
        ...rest,
        queryKey : ["Financeiro", "Titulo", "Listar", "Pagina", queryString],
        queryFn : () => Axios.get<IPagina<Titulo>>(`financeiro/titulo/pagina?${queryString}`).finally(onFinish),
        select : ({data}) => {
            data.registros = data.registros.map(m => {
                return {
                    ...m,
                    dataVencimento : m.dataVencimento === undefined ? undefined : new Date(m.dataVencimento as any),
                    //dataCredito : m.dataCredito == null ? null : new Date(m.dataCredito as any),
                    dataEmissao : m.dataEmissao == undefined ? null : new Date(m.dataEmissao as any),
                    dataPagamento : m.dataPagamento == undefined ? null : new Date(m.dataPagamento as any),
                    dataCriacao : new Date(m.dataCriacao as any),
                    fatura : m?.fatura === undefined ? undefined : {
                        ...m?.fatura,
                        dataEmissao : new Date(m.fatura.dataEmissao),
                        dataCriacao : new Date(m.fatura.dataCriacao),
                    }
                } as Titulo;
            });

            return data;
        },
        staleTime : 1000 * 60 * 0.05 // 30 segundos
    });
}

export function usePaginaMutation({
    onSuccess,
    ...rest
}:BasicMutationAxiosType<{
    pagina?:IPaginaInfo,
    titulo?:Titulo,
    ativo?:boolean,
    receita?:boolean
}, unknown, AxiosResponse<IPagina<Titulo>>>) {
    return useMutation({
        ...rest,
        mutationFn : ({pagina, titulo, ativo, receita}) => {
            const queryString = RequestUtilitario.objectToQueryString({
                pagina : (pagina ?? new PaginaInfo()),
                titulo : (titulo ?? {}),
                tituloAtivo : ativo,
                receita
            });
            return Axios.get<IPagina<Titulo>>(`financeiro/titulo/pagina?${queryString}`);/*.finally(onFinish)*/
        },
        onSuccess(response, variables, context) {
            const data = response.data ?? null;
            if (data != null)
            {
                data.registros.forEach(f => {
                    if (f.movimentacao?.tituloTarifa && !f.movimentacao.tituloTarifa.ativo)
                    {
                        f.movimentacao.tituloTarifa = null;
                    }
                    /// @ts-ignore
                    f.dataVencimento = f.dataVencimento === undefined ? undefined : new Date(f.dataVencimento as any);
                    /// @ts-ignore
                    f.dataEmissao = f.dataEmissao === undefined ? undefined : new Date(f.dataEmissao as any);
                    f.dataCredito = f.dataCredito == null ? null : new Date(f.dataCredito as any);
                    f.dataPagamento = f.dataPagamento == null ? null : new Date(f.dataPagamento as any);
                    f.dataCriacao =  new Date(f.dataCriacao as any);
                });
            }

            onSuccess?.(response, variables, context);
        }
    });
}

export function useBuscarMutation({
    onSuccess,
    ...rest
}:BasicMutationAxiosType<{codigo:number}, unknown, AxiosResponse<Titulo|null>>) {
    return useMutation({
        ...rest,
        mutationFn : ({codigo}) => Axios.get<Titulo|null>(`financeiro/titulo/${codigo}`),
        onSuccess(response, variables, context) {
            const data = response.data ?? null;
            if (data != null)
            {
                if (response.data?.movimentacao?.tituloTarifa && !response.data.movimentacao.tituloTarifa.ativo)
                {
                    response.data.movimentacao.tituloTarifa = null;
                }
                /// @ts-ignore
                data.dataVencimento = data.dataVencimento === undefined ? undefined : new Date(data.dataVencimento as any);
                /// @ts-ignore
                data.dataEmissao = data.dataEmissao === undefined ? undefined : new Date(data.dataEmissao as any);
                data.dataCredito = data.dataCredito == null ? null : new Date(data.dataCredito as any);
                data.dataPagamento = data.dataPagamento == null ? null : new Date(data.dataPagamento as any);
                data.dataCriacao =  new Date(data.dataCriacao as any);
            }

            onSuccess?.(response, variables, context);
        }
    });
}
export function useBuscar({
    codigo,
    select,
    ...rest
}:BasicQueryType<AxiosResponse<Titulo|null>, {
    codigo:number
}>) {
    return useQuery({
        ...rest,
        queryKey : ["Financeiro.Titulo.Listar", `Financeiro.Titulo.Buscar.${codigo}`],
        queryFn : () => Axios.get<Titulo|null>(`financeiro/titulo/${codigo}`),
        select : (response) => {
            let data = response.data ?? null;
            if (data != null)
            {
                data.dataVencimento = data.dataVencimento === undefined ? undefined : new Date(data.dataVencimento as any);
                data.dataEmissao = data.dataEmissao === undefined ? undefined : new Date(data.dataEmissao as any);
                //data.dataCredito = data.dataCredito == null ? null : new Date(data.dataCredito as any);
                data.dataPagamento = data.dataPagamento == null ? null : new Date(data.dataPagamento as any);
                data.dataCriacao =  new Date(data.dataCriacao as any);
            }
            data = data ?? null as Titulo|null;
            response = select?.(response) ?? response;
            return response;
        },
        staleTime : 1000 * 60 * 0.05 // 30 segundos
    });
}

export function useBuscarPorToken({
    identificador,
    select,
    ...rest
}:BasicQueryType<AxiosResponse<Titulo|null>, {
    identificador:string
}>) {
    return useQuery({
        ...rest,
        queryKey : ["Financeiro.Titulo.Listar", `Financeiro.Titulo.Buscar.Link.${identificador}`],
        queryFn : () => Axios.get<Titulo|null>(`financeiro/titulo/link?${identificador}`),
        select : (response) => {
            let data = response.data ?? null as Titulo|null;
            if (data != null)
            {
                data.dataVencimento = data.dataVencimento === undefined ? undefined : new Date(data.dataVencimento as any);
                //data.dataCredito = data.dataCredito == null ? null : new Date(data.dataCredito as any);
                data.dataPagamento = data.dataPagamento == null ? null : new Date(data.dataPagamento as any);
                data.dataCriacao =  new Date(data.dataCriacao as any);
                //data.tokenPagamento = response.headers["pagamento-token"];
            }
            response = select?.(response) ?? response;
            return response;
        },
        staleTime : 1000 * 60 * 0.05 // 1 minuto
    });
}

export function useGerarLink({
    ...rest
}:BasicMutationAxiosType<number, unknown, AxiosResponse<string>>) {
    return useMutation({
        ...rest,
        mutationFn : (codigo) => Axios.get<string>(`financeiro/titulo/link/gerar/${codigo}`)
    });
}

export const useTituloGerarLink = useGerarLink;

export default {
    useSalvar,
    useDesativar,
    useAtivar,
    useListar,
    useListarPagina,
    useGerarLink
}